import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Select from "react-select";

const StepAuthors = ({
  formData,
  handleInputChange,
  authors = [],
  loadingAuthors = false,
  onSearchAuthors,
  affiliations = [], // Nouveau prop pour les affiliations
  loadingAffiliations = false, // Nouveau prop pour le loading des affiliations
  requiredField,
}) => {
  const [authorSearch, setAuthorSearch] = useState("");
  const [coauthorSearch, setCoauthorSearch] = useState("");
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [selectedCoauthors, setSelectedCoauthors] = useState([]);
  const [showAuthorForm, setShowAuthorForm] = useState(false);
  const [showCoauthorForm, setShowCoauthorForm] = useState(false);

  // États pour les nouveaux auteurs avec affiliations
  const [newAuthor, setNewAuthor] = useState({
    first_name: "",
    last_name: "",
    email_user: "",
    title_aut: "",
    affiliations: [], // Changé pour supporter plusieurs affiliations
    orch_id: "",
  });

  const [newCoauthor, setNewCoauthor] = useState({
    first_name: "",
    last_name: "",
    email_user: "",
    title_aut: "",
    affiliations: [], // Changé pour supporter plusieurs affiliations
    orch_id: "",
  });

  // Mémoriser les options d'affiliations pour éviter les re-calculs
  const affiliationOptions = useMemo(() => {
    return affiliations.map((affiliation) => ({
      value: affiliation.id_aff,
      label: affiliation.name_aff,
      country: affiliation.country_aff,
      type: affiliation.type_aff,
      data: affiliation,
    }));
  }, [affiliations]);

  // Initialisation des données existantes
  useEffect(() => {
    if (formData.author) {
      setSelectedAuthor({
        value: formData.author.id_aut,
        label: `${formData.author.first_name} ${formData.author.last_name}`,
        data: formData.author,
      });
    }

    if (formData.coauthors && formData.coauthors.length > 0) {
      const coauthorOptions = formData.coauthors.map((coauthor) => ({
        value: coauthor.id_aut,
        label: `${coauthor.first_name} ${coauthor.last_name}`,
        data: coauthor,
      }));
      setSelectedCoauthors(coauthorOptions);
    }
  }, [formData.author, formData.coauthors]);

  // Options pour les auteurs - adapté au format de votre service
  const authorOptions = useMemo(() => {
    return authors.map((author) => ({
      value: author.id_aut,
      label: `${author.first_name} ${author.last_name}`,
      email: author.email_user,
      title: author.title_aut,
      affiliations: author.affiliations || [],
      data: author,
    }));
  }, [authors]);

  // Styles personnalisés pour React Select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#57A863" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 1px #57A863" : "none",
      "&:hover": {
        borderColor: "#57A863",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#57A863"
        : state.isFocused
        ? "#f0fdf4"
        : "white",
      color: state.isSelected ? "white" : "#374151",
      "&:hover": {
        backgroundColor: state.isSelected ? "#57A863" : "#f0fdf4",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#f0fdf4",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#166534",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#166534",
      "&:hover": {
        backgroundColor: "#57A863",
        color: "white",
      },
    }),
  };

  // Format des options avec email et titre
  const formatOptionLabel = ({ label, email, title, country }) => (
    <div>
      <div style={{ fontWeight: "500" }}>
        {title && `${title} `}{label}
      </div>
      {email && (
        <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
          📧 {email}
        </div>
      )}
      {country && (
        <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "1px" }}>
          🌍 {country}
        </div>
      )}
    </div>
  );

  // Format pour les affiliations
  const formatAffiliationLabel = ({ label, country, type }) => (
    <div>
      <div style={{ fontWeight: "500" }}>{label}</div>
      <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
        🌍 {country} {type && `• ${type}`}
      </div>
    </div>
  );

  // Gestionnaire pour la sélection de l'auteur principal
  const handleAuthorChange = useCallback((selectedOption) => {
    setSelectedAuthor(selectedOption);
    if (selectedOption) {
      handleInputChange("id_aut", null, selectedOption.value);
      handleInputChange("author", null, selectedOption.data);
    } else {
      handleInputChange("id_aut", null, null);
      handleInputChange("author", null, null);
    }
  }, [handleInputChange]);

  // Gestionnaire pour la sélection des co-auteurs
  const handleCoauthorsChange = useCallback((selectedOptions) => {
    setSelectedCoauthors(selectedOptions || []);
    const coauthorIds = selectedOptions
      ? selectedOptions.map((option) => option.value).join(",")
      : "";
    const coauthorsData = selectedOptions
      ? selectedOptions.map((option) => option.data)
      : [];

    handleInputChange("coauthor_ids", null, coauthorIds);
    handleInputChange("coauthors", null, coauthorsData);
  }, [handleInputChange]);

  // Gestionnaire pour les affiliations du nouvel auteur
  const handleNewAuthorAffiliationsChange = useCallback((selectedOptions) => {
    const affiliationIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setNewAuthor(prev => ({
      ...prev,
      affiliations: affiliationIds
    }));
  }, []);

  // Gestionnaire pour les affiliations du nouveau co-auteur
  const handleNewCoauthorAffiliationsChange = useCallback((selectedOptions) => {
    const affiliationIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setNewCoauthor(prev => ({
      ...prev,
      affiliations: affiliationIds
    }));
  }, []);

  // Gestionnaire pour ajouter un nouvel auteur
  const handleAddNewAuthor = useCallback(() => {
    if (newAuthor.first_name && newAuthor.last_name && newAuthor.email_user) {
      const tempId = `new_${Date.now()}`;
      const newAuthorOption = {
        value: tempId,
        label: `${newAuthor.first_name} ${newAuthor.last_name}`,
        email: newAuthor.email_user,
        title: newAuthor.title_aut,
        orch_id: newAuthor.orch_id,
        data: {
          ...newAuthor,
          id_aut: tempId,
          isNew: true,
          id_aff: newAuthor.affiliations.join(","), // Pour compatibilité
        },
      };

      setSelectedAuthor(newAuthorOption);
      handleInputChange("id_aut", null, tempId);
      handleInputChange("author", null, newAuthorOption.data);
      handleInputChange("new_author_data", null, newAuthor);

      // Réinitialiser le formulaire
      setNewAuthor({
        first_name: "",
        last_name: "",
        email_user: "",
        title_aut: "",
        affiliations: [],
        orch_id: "",
      });
      setShowAuthorForm(false);
    }
  }, [newAuthor, handleInputChange]);

  // Gestionnaire pour ajouter un nouveau co-auteur
  const handleAddNewCoauthor = useCallback(() => {
    if (newCoauthor.first_name && newCoauthor.last_name && newCoauthor.email_user) {
      const tempId = `new_coauthor_${Date.now()}`;
      const newCoauthorOption = {
        value: tempId,
        label: `${newCoauthor.first_name} ${newCoauthor.last_name}`,
        email: newCoauthor.email_user,
        title: newCoauthor.title_aut,
        orch_id: newCoauthor.orch_id,
        data: {
          ...newCoauthor,
          id_aut: tempId,
          isNew: true,
          id_aff: newCoauthor.affiliations.join(","), // Pour compatibilité
        },
      };

      const updatedCoauthors = [...selectedCoauthors, newCoauthorOption];
      setSelectedCoauthors(updatedCoauthors);

      const coauthorIds = updatedCoauthors
        .map((option) => option.value)
        .join(",");
      const coauthorsData = updatedCoauthors.map((option) => option.data);

      handleInputChange("coauthor_ids", null, coauthorIds);
      handleInputChange("coauthors", null, coauthorsData);

      // Ajouter les données du nouveau co-auteur
      const existingNewCoauthors = formData.new_coauthors_data || [];
      handleInputChange("new_coauthors_data", null, [
        ...existingNewCoauthors,
        newCoauthor,
      ]);

      // Réinitialiser le formulaire
      setNewCoauthor({
        first_name: "",
        last_name: "",
        email_user: "",
        title_aut: "",
        affiliations: [],
        orch_id: "",
      });
      setShowCoauthorForm(false);
    }
  }, [newCoauthor, selectedCoauthors, handleInputChange, formData.new_coauthors_data]);

  // Obtenir les affiliations sélectionnées pour l'affichage
  const getSelectedAffiliations = (affiliationIds) => {
    return affiliationOptions.filter(option => 
      affiliationIds.includes(option.value)
    );
  };

  return (
    <div className="step-content">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-700">
          Auteurs et collaborateurs
        </h3>
        <p className="mt-2 text-sm text-gray-600">
          Définissez l'auteur principal et ajoutez les co-auteurs de l'article.
        </p>
      </div>

      <div className="space-y-8">
        {/* 1. AUTEUR PRINCIPAL */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="mb-4">
            <h4 className="text-lg font-medium text-gray-800">
              👤 Auteur principal {requiredField}
            </h4>
            <p className="text-sm text-gray-500">
              Sélectionnez l'auteur principal de l'article ou créez un nouveau profil.
            </p>
          </div>

          <div className="space-y-4">
            <Select
              value={selectedAuthor}
              onChange={handleAuthorChange}
              options={authorOptions}
              formatOptionLabel={formatOptionLabel}
              styles={customSelectStyles}
              placeholder="Rechercher un auteur existant..."
              noOptionsMessage={() => "Aucun auteur trouvé"}
              loadingMessage={() => "Recherche en cours..."}
              isLoading={loadingAuthors}
              isClearable
              isSearchable
              onInputChange={(inputValue) => {
                setAuthorSearch(inputValue);
                if (onSearchAuthors && inputValue.length > 2) {
                  onSearchAuthors(inputValue);
                }
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />

            <div className="flex items-center justify-center">
              <div className="flex-1 border-t border-gray-200"></div>
              <span className="mx-4 text-sm text-gray-500">ou</span>
              <div className="flex-1 border-t border-gray-200"></div>
            </div>

            <button
              type="button"
              onClick={() => setShowAuthorForm(!showAuthorForm)}
              className="w-full rounded-md border-2 border-dashed border-[#57A863] bg-green-50 px-4 py-3 text-sm font-medium text-[#57A863] hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-[#57A863]/50"
            >
              {showAuthorForm ? "Annuler" : "+ Créer un nouvel auteur"}
            </button>

            {showAuthorForm && (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom {requiredField}
                    </label>
                    <input
                      type="text"
                      value={newAuthor.first_name}
                      onChange={(e) =>
                        setNewAuthor({ ...newAuthor, first_name: e.target.value })
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                      placeholder="Prénom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom {requiredField}
                    </label>
                    <input
                      type="text"
                      value={newAuthor.last_name}
                      onChange={(e) =>
                        setNewAuthor({ ...newAuthor, last_name: e.target.value })
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                      placeholder="Nom"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email {requiredField}
                  </label>
                  <input
                    type="email"
                    value={newAuthor.email_user}
                    onChange={(e) =>
                      setNewAuthor({ ...newAuthor, email_user: e.target.value })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                    placeholder="email@exemple.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titre
                    </label>
                    <select
                      value={newAuthor.title_aut}
                      onChange={(e) =>
                        setNewAuthor({ ...newAuthor, title_aut: e.target.value })
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                    >
                      <option value="">Sélectionner un titre</option>
                      <option value="Dr">Dr</option>
                      <option value="Pr">Pr</option>
                      <option value="M.">M.</option>
                      <option value="Mme">Mme</option>
                      <option value="Mlle">Mlle</option>
                      <option value="IE">IE</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ORCID de l'auteur
                    </label>
                    <input
                      type="text"
                      value={newAuthor.orch_id}
                      onChange={(e) =>
                        setNewAuthor({ ...newAuthor, orch_id: e.target.value })
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                      placeholder="0000-0001-2345-6789"
                    />
                  </div>
                </div>

                {/* Sélection des affiliations */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Affiliations
                  </label>
                  <Select
                    isMulti
                    value={getSelectedAffiliations(newAuthor.affiliations)}
                    onChange={handleNewAuthorAffiliationsChange}
                    options={affiliationOptions}
                    formatOptionLabel={formatAffiliationLabel}
                    styles={customSelectStyles}
                    placeholder="Sélectionnez les affiliations..."
                    noOptionsMessage={() => "Aucune affiliation trouvée"}
                    loadingMessage={() => "Chargement des affiliations..."}
                    isLoading={loadingAffiliations}
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Vous pouvez sélectionner plusieurs affiliations
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleAddNewAuthor}
                    disabled={!newAuthor.first_name || !newAuthor.last_name || !newAuthor.email_user}
                    className="flex-1 rounded-md bg-[#57A863] px-4 py-2 text-sm font-medium text-white hover:bg-[#4a8f56] focus:outline-none focus:ring-2 focus:ring-[#57A863]/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Ajouter cet auteur
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAuthorForm(false)}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500/50"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Affichage de l'auteur sélectionné */}
            {selectedAuthor && (
              <div className="rounded-md border border-green-200 bg-green-50 p-4">
                <div className="flex items-start">
                  <svg
                    className="mr-3 h-5 w-5 flex-shrink-0 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-800">
                      👤 Auteur principal sélectionné : {selectedAuthor.title && `${selectedAuthor.title} `}{selectedAuthor.label}
                    </p>
                    {selectedAuthor.email && (
                      <p className="mt-1 text-xs text-green-600">
                        📧 {selectedAuthor.email}
                      </p>
                    )}
                    {selectedAuthor.data?.affiliations && selectedAuthor.data.affiliations.length > 0 && (
                      <div className="mt-1">
                        <p className="text-xs text-green-600">
                          🏢 Affiliations: {selectedAuthor.data.affiliations.map(aff => 
                            typeof aff === 'object' ? aff.name_aff : aff
                          ).join(', ')}
                        </p>
                      </div>
                    )}
                    {selectedAuthor.data?.isNew && (
                      <p className="mt-1 text-xs text-green-600">
                        ✨ Nouvel auteur (sera créé lors de la soumission)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 2. CO-AUTEURS */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <div className="mb-4">
            <h4 className="text-lg font-medium text-gray-800">
              👥 Co-auteurs
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({selectedCoauthors.length} sélectionné{selectedCoauthors.length !== 1 ? 's' : ''})
              </span>
            </h4>
            <p className="text-sm text-gray-500">
              Ajoutez les co-auteurs de l'article (optionnel).
            </p>
          </div>

          <div className="space-y-4">
            <Select
              isMulti
              value={selectedCoauthors}
              onChange={handleCoauthorsChange}
              options={authorOptions.filter(option => option.value !== selectedAuthor?.value)}
              formatOptionLabel={formatOptionLabel}
              styles={customSelectStyles}
              placeholder="Rechercher des co-auteurs..."
              noOptionsMessage={() => "Aucun co-auteur trouvé"}
              loadingMessage={() => "Recherche en cours..."}
              isLoading={loadingAuthors}
              isSearchable
              onInputChange={(inputValue) => {
                setCoauthorSearch(inputValue);
                if (onSearchAuthors && inputValue.length > 2) {
                  onSearchAuthors(inputValue);
                }
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />

            <div className="flex items-center justify-center">
              <div className="flex-1 border-t border-gray-200"></div>
              <span className="mx-4 text-sm text-gray-500">ou</span>
              <div className="flex-1 border-t border-gray-200"></div>
            </div>

            <button
              type="button"
              onClick={() => setShowCoauthorForm(!showCoauthorForm)}
              className="w-full rounded-md border-2 border-dashed border-[#57A863] bg-green-50 px-4 py-3 text-sm font-medium text-[#57A863] hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-[#57A863]/50"
            >
              {showCoauthorForm ? "Annuler" : "+ Ajouter un nouveau co-auteur"}
            </button>

            {showCoauthorForm && (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom {requiredField}
                    </label>
                    <input
                      type="text"
                      value={newCoauthor.first_name}
                      onChange={(e) =>
                        setNewCoauthor({ ...newCoauthor, first_name: e.target.value })
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                      placeholder="Prénom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom {requiredField}
                    </label>
                    <input
                      type="text"
                      value={newCoauthor.last_name}
                      onChange={(e) =>
                        setNewCoauthor({ ...newCoauthor, last_name: e.target.value })
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                      placeholder="Nom"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email {requiredField}
                  </label>
                  <input
                    type="email"
                    value={newCoauthor.email_user}
                    onChange={(e) =>
                      setNewCoauthor({ ...newCoauthor, email_user: e.target.value })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                    placeholder="email@exemple.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titre
                    </label>
                    <select
                      value={newCoauthor.title_aut}
                      onChange={(e) =>
                        setNewCoauthor({ ...newCoauthor, title_aut: e.target.value })
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                    >
                      <option value="">Sélectionner un titre</option>
                      <option value="Dr">Dr</option>
                      <option value="Pr">Pr</option>
                      <option value="M.">M.</option>
                      <option value="Mme">Mme</option>
                      <option value="Mlle">Mlle</option>
                      <option value="IE">IE</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ORCID de l'auteur
                    </label>
                    <input
                      type="text"
                      value={newCoauthor.orch_id}
                      onChange={(e) =>
                        setNewCoauthor({ ...newCoauthor, orch_id: e.target.value })
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#57A863] focus:outline-none focus:ring-1 focus:ring-[#57A863]"
                      placeholder="0000-0001-2345-6789"
                    />
                  </div>
                </div>

                {/* Sélection des affiliations pour co-auteur */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Affiliations
                  </label>
                  <Select
                    isMulti
                    value={getSelectedAffiliations(newCoauthor.affiliations)}
                    onChange={handleNewCoauthorAffiliationsChange}
                    options={affiliationOptions}
                    formatOptionLabel={formatAffiliationLabel}
                    styles={customSelectStyles}
                    placeholder="Sélectionnez les affiliations..."
                    noOptionsMessage={() => "Aucune affiliation trouvée"}
                    loadingMessage={() => "Chargement des affiliations..."}
                    isLoading={loadingAffiliations}
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Vous pouvez sélectionner plusieurs affiliations
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleAddNewCoauthor}
                    disabled={!newCoauthor.first_name || !newCoauthor.last_name || !newCoauthor.email_user}
                    className="flex-1 rounded-md bg-[#57A863] px-4 py-2 text-sm font-medium text-white hover:bg-[#4a8f56] focus:outline-none focus:ring-2 focus:ring-[#57A863]/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Ajouter ce co-auteur
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCoauthorForm(false)}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500/50"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Affichage des co-auteurs sélectionnés */}
            {selectedCoauthors.length > 0 && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-start">
                  <svg
                    className="mr-3 h-5 w-5 flex-shrink-0 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-800">
                      👥 Co-auteurs sélectionnés ({selectedCoauthors.length}) :
                    </p>
                    <div className="mt-2 space-y-1">
                      {selectedCoauthors.map((coauthor, index) => (
                        <div key={coauthor.value} className="text-xs text-blue-600">
                          {index + 1}. {coauthor.title && `${coauthor.title} `}{coauthor.label}
                          {coauthor.email && ` (${coauthor.email})`}
                          {coauthor.data?.isNew && (
                            <span className="ml-1 text-blue-500">✨ Nouveau</span>
                          )}
                          {coauthor.data?.affiliations && coauthor.data.affiliations.length > 0 && (
                            <div className="ml-2 text-xs text-blue-500">
                              🏢 {coauthor.data.affiliations.map(aff => 
                                typeof aff === 'object' ? aff.name_aff : aff
                              ).join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 3. INFORMATIONS COMPLÉMENTAIRES */}
        <div className="rounded-lg border border-gray-200 bg-blue-50 p-4">
          <div className="flex items-start">
            <svg
              className="mr-3 h-5 w-5 flex-shrink-0 text-blue-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <h5 className="text-sm font-medium text-blue-800">
                Informations importantes
              </h5>
              <ul className="mt-2 space-y-1 text-xs text-blue-600">
                <li>• L'auteur principal est obligatoire pour soumettre l'article</li>
                <li>• Les co-auteurs peuvent être ajoutés ou modifiés ultérieurement</li>
                <li>• Les nouveaux auteurs seront créés automatiquement lors de la soumission</li>
                <li>• Vérifiez les emails pour éviter les doublons</li>
                <li>• Les affiliations multiples sont supportées pour chaque auteur</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

StepAuthors.propTypes = {
  formData: PropTypes.shape({
    id_aut: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    author: PropTypes.object,
    orch_id: PropTypes.string,
    coauthor_ids: PropTypes.string,
    coauthors: PropTypes.array,
    new_author_data: PropTypes.object,
    new_coauthors_data: PropTypes.array,
  }).isRequired,
  handleInputChange: PropTypes.func.isRequired,
  authors: PropTypes.array,
  loadingAuthors: PropTypes.bool,
  onSearchAuthors: PropTypes.func,
  affiliations: PropTypes.array, // Nouveau prop
  loadingAffiliations: PropTypes.bool, // Nouveau prop
  requiredField: PropTypes.element.isRequired,
};

export default React.memo(StepAuthors);